import PropTypes from "prop-types";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Divider from "@mui/material/Divider";
import Icon from "@mui/material/Icon";

import MKBox from "components/MKBox";
import MKTypography from "components/MKTypography";
import MKButton from "components/MKButton";

import DefaultNavbar from "examples/Navbars/DefaultNavbar";
import DefaultFooter from "examples/Footers/DefaultFooter";

import footerRoutes from "footer_simple.routes";

import commandCenterImg from "assets/images/technology/commandcenterscreenshot.png";
import mobileImg from "assets/images/technology/mobilescreenshot.png";
import eveImg from "assets/images/technology/aiassistant.png";
import messengerImg from "assets/images/technology/messengerscreenshot.png";
import messagingFlowImg from "assets/images/technology/unifiedmessagingflow.png";

function FeatureItem({ icon, text }) {
  return (
    <MKBox display="flex" alignItems="flex-start" mb={1.5}>
      <Icon sx={{ color: "info.main", mr: 1, mt: "2px", fontSize: "1.1rem" }}>{icon}</Icon>
      <MKTypography variant="body2" color="text">{text}</MKTypography>
    </MKBox>
  );
}

FeatureItem.propTypes = {
  icon: PropTypes.string.isRequired,
  text: PropTypes.string.isRequired,
};

function Technology() {
  return (
    <>
      <DefaultNavbar routes={[]} sticky />
      <MKBox component="section" pt={14} pb={8}>
        <Container>
          <Grid container justifyContent="center">
            <Grid item xs={12} md={10} lg={9}>

              {/* Header card */}
              <Card>
                <MKBox
                  variant="gradient"
                  bgColor="dark"
                  borderRadius="lg"
                  coloredShadow="dark"
                  p={3}
                  mt={-3}
                  mx={2}
                >
                  <MKTypography variant="h3" color="white">
                    Technology-Driven Dispatching
                  </MKTypography>
                  <MKTypography variant="body2" color="white" opacity={0.8}>
                    D Harris Tours, Inc. &mdash; Proprietary fleet management platform built
                    for modern transportation operations.
                  </MKTypography>
                </MKBox>

                <MKBox pb={6} px={{ xs: 3, md: 6 }}>
                  <MKTypography variant="body2" color="text" mt={4} mb={2}>
                    Over more than two decades of service, D Harris Tours has invested in building
                    a fully integrated, proprietary technology platform that keeps every bus on
                    schedule, every driver connected, and every customer informed. From real-time
                    GPS dispatch to AI-powered driver assistance, our tools give us an operational
                    edge that translates directly into reliability for our customers.
                  </MKTypography>

                  <Divider sx={{ my: 4 }} />

                  {/* Dispatch Command */}
                  <MKTypography variant="h4" mb={1}>Dispatch Command</MKTypography>
                  <MKTypography variant="body2" color="text" mb={3}>
                    Our web-based command center gives dispatchers a live map view of every active
                    job across the fleet. Late departure alerts surface automatically so issues are
                    caught before they become problems.
                  </MKTypography>
                  <MKBox
                    component="img"
                    src={commandCenterImg}
                    alt="Dispatch Command screenshot"
                    width="100%"
                    borderRadius="lg"
                    shadow="md"
                    mb={3}
                    sx={{ border: "1px solid", borderColor: "grey.200" }}
                  />
                  <Grid container spacing={2} mb={2}>
                    <Grid item xs={12} sm={6}>
                      <FeatureItem icon="map" text="Real-time GPS map with all active jobs" />
                      <FeatureItem icon="alarm" text="Automatic late departure alerts" />
                      <FeatureItem icon="directions_bus" text="Per-driver status monitoring" />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FeatureItem icon="route" text="One-click route planning and mapping" />
                      <FeatureItem icon="filter_list" text="Filter by active, reserved, or all jobs" />
                      <FeatureItem icon="notifications_active" text="Snooze and escalation controls" />
                    </Grid>
                  </Grid>

                  <Divider sx={{ my: 4 }} />

                  {/* Driver Mobile App */}
                  <MKTypography variant="h4" mb={1}>Driver Mobile App</MKTypography>
                  <MKTypography variant="body2" color="text" mb={3}>
                    Drivers stay connected through a purpose-built iOS and Android app. Clock in,
                    view assignments, update trip status, and communicate with dispatch — all from
                    one place.
                  </MKTypography>
                  <Grid container spacing={4} alignItems="flex-start" mb={2}>
                    <Grid item xs={12} sm={5} md={4}>
                      <MKBox
                        component="img"
                        src={mobileImg}
                        alt="Driver mobile app screenshot"
                        width="100%"
                        maxWidth="260px"
                        borderRadius="xl"
                        shadow="lg"
                        display="block"
                        mx="auto"
                        sx={{ border: "1px solid", borderColor: "grey.200" }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={7} md={8}>
                      <FeatureItem icon="login" text="Clock in / clock out with GPS verification" />
                      <FeatureItem icon="assignment" text="View today's jobs and full trip details" />
                      <FeatureItem icon="navigation" text="Turn-by-turn navigation to pickup location" />
                      <FeatureItem icon="sync" text="Live status updates pushed to dispatch" />
                      <FeatureItem icon="schedule" text="Upcoming job preview with passenger count" />
                      <FeatureItem icon="offline_bolt" text="Works in low-connectivity conditions" />
                    </Grid>
                  </Grid>

                  <Divider sx={{ my: 4 }} />

                  {/* Eve AI Assistant */}
                  <MKTypography variant="h4" mb={1}>Eve &mdash; AI Driving Assistant</MKTypography>
                  <MKTypography variant="body2" color="text" mb={3}>
                    Eve is our built-in AI assistant for drivers. Hands-free voice mode lets drivers
                    ask about their schedule, get trip details, and receive updates without touching
                    their phone.
                  </MKTypography>
                  <Grid container spacing={4} alignItems="flex-start" mb={2}>
                    <Grid item xs={12} sm={5} md={4}>
                      <MKBox
                        component="img"
                        src={eveImg}
                        alt="Eve AI assistant screenshot"
                        width="100%"
                        maxWidth="260px"
                        borderRadius="xl"
                        shadow="lg"
                        display="block"
                        mx="auto"
                        sx={{ border: "1px solid", borderColor: "grey.200" }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={7} md={8}>
                      <FeatureItem icon="mic" text="&ldquo;Hey Eve&rdquo; wake word — fully hands-free" />
                      <FeatureItem icon="smart_toy" text="Ask about upcoming trips, schedules, or requirements" />
                      <FeatureItem icon="record_voice_over" text="Voice responses keep drivers eyes-on-road" />
                      <FeatureItem icon="psychology" text="Context-aware answers based on driver's actual jobs" />
                      <FeatureItem icon="chat" text="Text mode available as fallback" />
                    </Grid>
                  </Grid>

                  <Divider sx={{ my: 4 }} />

                  {/* Messenger + Unified Messaging */}
                  <MKTypography variant="h4" mb={1}>D Harris Messenger &amp; Unified Alerts</MKTypography>
                  <MKTypography variant="body2" color="text" mb={3}>
                    Dispatchers send messages through the desktop Messenger app. Every message is
                    delivered through three redundant channels simultaneously — so drivers always
                    get the word.
                  </MKTypography>
                  <Grid container spacing={4} alignItems="flex-start" mb={3}>
                    <Grid item xs={12} sm={5} md={4}>
                      <MKBox
                        component="img"
                        src={messengerImg}
                        alt="D Harris Messenger screenshot"
                        width="100%"
                        maxWidth="260px"
                        borderRadius="lg"
                        shadow="lg"
                        display="block"
                        mx="auto"
                        sx={{ border: "1px solid", borderColor: "grey.200" }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={7} md={8}>
                      <FeatureItem icon="forum" text="Per-driver or broadcast messaging from dispatch" />
                      <FeatureItem icon="radio" text="Push-to-talk voice radio — instant group communication" />
                      <FeatureItem icon="sms" text="SMS fallback ensures delivery even without data" />
                      <FeatureItem icon="notifications" text="Mobile push alerts with sound and vibration" />
                      <FeatureItem icon="history" text="Full message history and read receipts" />
                    </Grid>
                  </Grid>
                  <MKBox
                    component="img"
                    src={messagingFlowImg}
                    alt="Unified messaging flow diagram"
                    width="100%"
                    maxWidth="600px"
                    display="block"
                    mx="auto"
                    mb={2}
                  />

                  <Divider sx={{ my: 4 }} />

                  {/* SaaS / Licensing pitch */}
                  <MKBox
                    variant="gradient"
                    bgColor="info"
                    borderRadius="lg"
                    p={{ xs: 3, md: 5 }}
                    mt={2}
                  >
                    <MKTypography variant="h4" color="white" mb={1}>
                      Available for Your Fleet
                    </MKTypography>
                    <MKTypography variant="body2" color="white" opacity={0.9} mb={3}>
                      The Simple platform — Dispatch Command, the Driver Mobile App, Eve AI, and
                      D Harris Messenger — is available for licensing by other transportation
                      companies. Whether you run 5 buses or 500, the platform scales to your
                      operation.
                    </MKTypography>
                    <Grid container spacing={2} mb={3}>
                      {[
                        { icon: "speed", text: "Cut late departures with real-time alerting" },
                        { icon: "people", text: "Keep every driver connected and accountable" },
                        { icon: "insights", text: "Operational data and trip history at your fingertips" },
                        { icon: "lock", text: "Your data, your branding, your platform" },
                      ].map(({ icon, text }) => (
                        <Grid item xs={12} sm={6} key={text}>
                          <MKBox display="flex" alignItems="center">
                            <Icon sx={{ color: "white", mr: 1 }}>{icon}</Icon>
                            <MKTypography variant="body2" color="white" opacity={0.9}>{text}</MKTypography>
                          </MKBox>
                        </Grid>
                      ))}
                    </Grid>
                    <MKTypography variant="body2" color="white" opacity={0.85} mb={3}>
                      Pricing is based on fleet size and feature set. Contact us to schedule a
                      demo or discuss a custom deployment for your company.
                    </MKTypography>
                    <MKBox display="flex" gap={2} flexWrap="wrap">
                      <MKButton
                        component="a"
                        href="mailto:juanaharrisdht@att.net?subject=Simple Platform Inquiry"
                        variant="contained"
                        color="white"
                        size="large"
                      >
                        Request a Demo
                      </MKButton>
                      <MKButton
                        component="a"
                        href="tel:+14159028542"
                        variant="outlined"
                        color="white"
                        size="large"
                      >
                        Call Us
                      </MKButton>
                    </MKBox>
                  </MKBox>

                </MKBox>
              </Card>

            </Grid>
          </Grid>
        </Container>
      </MKBox>
      <MKBox pt={6} px={1} mt={6}>
        <DefaultFooter content={footerRoutes} />
      </MKBox>
    </>
  );
}

export default Technology;
